import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { login } from "utils/auth";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import AuthShell, {
  PasswordInput,
  codeOf,
  go,
  goAfterAuth,
  isPlausiblePhone,
  normalizePhone,
  readableError,
  startCodeTimer,
} from "./auth/AuthShell";

const LOGIN = gql`
  mutation adminLogin($emailOrPhoneNumber: String!, $password: String!) {
    adminLogin(input: { emailOrPhoneNumber: $emailOrPhoneNumber, password: $password }) {
      type
      shop {
        _id
        verified
        isRejected
        rejectionMessage
        rootCategory {
          _id
          title
        }
        stripeAccountId
      }
      token {
        accessToken
        refreshToken
      }
      fullName
      roles {
        permissions {
          name
        }
      }
    }
  }
`;

const GET_VERIFICATION_CODE = gql`
  mutation getShopAdminPhoneVerificationCode($phoneNumber: String!) {
    getShopAdminPhoneVerificationCode(phoneNumber: $phoneNumber) {
      phoneVerificationCodeExpireTime
    }
  }
`;

/**
 * Where a signed-in admin belongs, and what the panel needs to know
 * when they get there.
 *
 * Shop admins are only finished when the shop exists, has been
 * verified and has payment details; anything missing sends them back
 * into onboarding at the point they stopped. Returns null for an
 * account type this panel has no home for, so the caller can say so
 * instead of leaving the button spinning — which is what the previous
 * version did.
 */
const applyLoginDestination = (admin) => {
  const storage = window.localStorage;

  if (admin.type === "SUPER-ADMIN") {
    const permissions = [];
    (admin.roles || []).forEach((role) => {
      (role.permissions || []).forEach((permission) => permissions.push(permission.name));
    });
    storage.setItem("permissions", JSON.stringify(permissions));
    return "/superAdmin/dashboard";
  }

  if (admin.type !== "SHOP-ADMIN") return null;

  storage.setItem("permissions", JSON.stringify([]));

  const shop = admin.shop;
  if (!shop) {
    storage.removeItem("shopId");
    storage.removeItem("shopType");
    storage.setItem("nextStep", "/addShop");
    return "/rules";
  }

  // The panel is themed per shop type, and reads it from here on load.
  const shopType = shop.rootCategory && shop.rootCategory.title;
  if (shopType) storage.setItem("shopType", String(shopType));

  // Payment details are the last thing standing between a working shop
  // and getting paid, so an account without them lands there rather
  // than on the dashboard.
  const destination = shop.stripeAccountId ? "/shopAdmin/dashboard" : "/api/v1/paymentMethod";

  // Verification supersedes an earlier rejection: a shop that fixed
  // what was wrong and got approved should not be sent back to the
  // edit form for the rest of time.
  if (shop.verified) return destination;

  if (shop.isRejected) {
    if (shop.rejectionMessage) storage.setItem("rejectionMessage", shop.rejectionMessage);
    storage.setItem("nextStep", destination);
    return "/editShop";
  }

  storage.setItem("nextStep", destination);
  return "/rules";
};

/**
 * ShopLogin - the front door for shop and restaurant owners.
 *
 * Signing in and starting an account share one card as two tabs rather
 * than sitting side by side, which put a lone phone field next to a
 * full sign-in form and left a 99px gap under it to line the buttons
 * up.
 */
const ShopLogin = (props) => {
  const history = props.history;
  const search = (props.location && props.location.search) || "";
  const [tab, setTab] = useState(search.indexOf("tab=signup") !== -1 ? "signup" : "signin");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [signInError, setSignInError] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [signUpError, setSignUpError] = useState("");
  const [signingUp, setSigningUp] = useState(false);

  const [adminLogin] = useMutation(LOGIN);
  const [getShopAdminPhoneVerificationCode] = useMutation(GET_VERIFICATION_CODE);

  useEffect(() => {
    const storage = window.localStorage;

    // Landing here means starting a new session; anything left from the
    // last one would otherwise ride along on the first request.
    storage.removeItem("shopId");
    storage.removeItem("refreshToken");
    storage.removeItem("token");
    storage.removeItem("shopType");
    storage.removeItem("verificationCode");
    storage.removeItem("phoneNumber");
    storage.removeItem("permissions");

    // "Remember me" used to keep the password in localStorage under a
    // base64 wrapper, which is storage, not encryption. Only the
    // identifier is remembered now, and any password left behind by the
    // old version is cleared out on the way past.
    storage.removeItem("password");

    const saved = storage.getItem("userName");
    if (saved) {
      try {
        setEmail(atob(saved));
        setRemember(true);
      } catch (error) {
        storage.removeItem("userName");
      }
    }
  }, []);

  const switchTo = (next) => {
    setTab(next);
    setSignInError("");
    setSignUpError("");
    if (history) history.replace(next === "signup" ? "/login?tab=signup" : "/login");
  };

  const onSubmitSignIn = (event) => {
    if (event) event.preventDefault();
    if (signingIn) return;

    const identifier = email.trim();
    setEmailError(identifier ? "" : "Enter your email or phone number");
    setPasswordError(password ? "" : "Enter your password");
    setSignInError("");
    if (!identifier || !password) return;

    setSigningIn(true);
    adminLogin({ variables: { emailOrPhoneNumber: identifier, password } })
      .then((res) => {
        const admin = res.data && res.data.adminLogin;
        if (!admin || !admin.token) {
          setSigningIn(false);
          setSignInError("We could not sign you in. Please try again.");
          return;
        }

        const destination = applyLoginDestination(admin);
        if (!destination) {
          setSigningIn(false);
          setSignInError("This account cannot be used with the admin panel.");
          return;
        }

        login(admin.token.accessToken.toString(), admin.token.refreshToken.toString());

        if (remember) window.localStorage.setItem("userName", btoa(identifier));
        else window.localStorage.removeItem("userName");

        goAfterAuth(destination);
      })
      .catch((error) => {
        setSigningIn(false);
        const code = codeOf(error);
        // 404 is "no such account" and 400 is "wrong password"; anything
        // else — a suspended admin, an unreachable server — used to
        // leave both fields blank and no explanation anywhere.
        setEmailError(code === "404" ? "We could not find that account" : "");
        setPasswordError(code === "400" ? "Wrong password" : "");
        setSignInError(code === "404" || code === "400" ? "" : readableError(error));
      });
  };

  const onSubmitSignUp = (event) => {
    if (event) event.preventDefault();
    if (signingUp) return;

    const trimmed = phoneNumber.trim();
    setSignUpError("");
    if (!trimmed) {
      setPhoneError("Enter your phone number");
      return;
    }
    if (!isPlausiblePhone(trimmed)) {
      setPhoneError("That does not look like a phone number");
      return;
    }
    setPhoneError("");

    const normalized = normalizePhone(trimmed);
    setSigningUp(true);
    getShopAdminPhoneVerificationCode({ variables: { phoneNumber: normalized } })
      .then((res) => {
        window.localStorage.setItem("phoneNumber", normalized);
        startCodeTimer(
          res.data.getShopAdminPhoneVerificationCode.phoneVerificationCodeExpireTime
        );
        go(history, "/submitCode");
      })
      .catch((error) => {
        setSigningUp(false);
        // The server's own wording covers the case that actually comes
        // up here — a phone number that already has an account.
        setPhoneError(readableError(error));
      });
  };

  return (
    <AuthShell
      title={tab === "signin" ? "Welcome back" : "Create your account"}
      subtitle={
        tab === "signin"
          ? "Sign in to manage your shop, menu and orders."
          : "Start with your phone number — we will text you a code."
      }
      brandTitle="Run your shop on Spark"
      brandText={
        tab === "signin"
          ? "Menus, orders, promotions, deliveries and payouts, in one place."
          : undefined
      }
      currentStep={tab === "signup" ? 0 : undefined}
    >
      <div className="authTabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "signin"}
          className={`authTab ${tab === "signin" ? "isActive" : ""}`}
          onClick={() => switchTo("signin")}
        >
          Sign in
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "signup"}
          className={`authTab ${tab === "signup" ? "isActive" : ""}`}
          onClick={() => switchTo("signup")}
        >
          Create account
        </button>
      </div>

      {tab === "signin" ? (
        <form className="authForm" onSubmit={onSubmitSignIn}>
          {signInError && (
            <div className="dsFormMessage error" role="alert">
              <ErrorOutlineIcon />
              <span>{signInError}</span>
            </div>
          )}

          <Field label="Email or phone number" htmlFor="loginIdentifier" error={emailError}>
            <input
              id="loginIdentifier"
              className="dsInput"
              type="text"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Password" htmlFor="loginPassword" error={passwordError}>
            <PasswordInput
              id="loginPassword"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <div className="authMinorRow">
            <label className="authCheckbox">
              <input
                type="checkbox"
                checked={remember}
                onChange={() => setRemember((current) => !current)}
              />
              Remember my email
            </label>
            <button
              type="button"
              className="authLink"
              onClick={() => go(history, "/forgetPassword")}
            >
              I forgot my password
            </button>
          </div>

          <Button type="submit" variant="primary" size="lg" block loading={signingIn}>
            {signingIn ? "Signing in" : "Sign in"}
          </Button>
        </form>
      ) : (
        <form className="authForm" onSubmit={onSubmitSignUp}>
          {signUpError && (
            <div className="dsFormMessage error" role="alert">
              <ErrorOutlineIcon />
              <span>{signUpError}</span>
            </div>
          )}

          <Field
            label="Phone number"
            htmlFor="signupPhone"
            error={phoneError}
            hint="We send a five-digit code to this number to confirm it is yours."
          >
            <input
              id="signupPhone"
              className="dsInput"
              type="tel"
              autoComplete="tel"
              placeholder="+1 604 555 0134"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
            />
          </Field>

          <Button type="submit" variant="primary" size="lg" block loading={signingUp}>
            {signingUp ? "Sending code" : "Send me a code"}
          </Button>

          <div className="authFoot">
            Already have an account?{" "}
            <button type="button" className="authLink" onClick={() => switchTo("signin")}>
              Sign in
            </button>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

export default ShopLogin;
